import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import { useAuth } from '../../hooks/useAuth';

// Ambassador Program — Sprint 21.3. Replaces the manual "Picklr Ambassador Log
// 2026" Google Form + spreadsheet review with a real approval queue and
// roster. See docs/COMS_Ambassador_Program_Build_Spec.md.
// v1 scope deliberately stops at roster + approval — contract upload/editing
// and quarterly review scheduling are the next pass, not built here yet.

const TABS = [
  { key: 'pending', label: 'Pending Activity' },
  { key: 'roster',  label: 'Roster' },
];
// Admin-only for now (BK, 10/22/26: "only me until we get this built out more"). The page is called Comps and Ambassadors for admins.
const OVERVIEW_TAB = { key: 'overview', label: 'Free Memberships vs Cap' };
const CLUB_NAMES = { LEW: 'Lewisville', MCK: 'McKinney', GB: 'Green Bay', MF: 'Menomonee Falls', WA: 'West Allis', ALL: 'Five clubs' };
const CLUB_ORDER = ['LEW', 'MCK', 'MF', 'WA', 'GB', 'ALL'];
const FLAG_STYLE = {
  green: { bg: '#16a34a22', fg: '#22c55e', label: 'At or under cap' },
  amber: { bg: '#d9770622', fg: '#f59e0b', label: 'Up to 25% over' },
  red:   { bg: '#dc262622', fg: '#ef4444', label: 'Over cap' },
};

function FlagPill({ flag }) {
  const f = FLAG_STYLE[flag];
  if (!f) return <span style={{ color: '#666', fontSize: 11 }}>No cap set</span>;
  return <span style={{ background: f.bg, color: f.fg, border: `1px solid ${f.fg}`, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{f.label}</span>;
}

function CapEditor({ club, current, onSaved }) {
  const { post } = useApi();
  const [val, setVal] = useState(current ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setVal(current ?? ''); }, [current]);
  const dirty = String(val) !== String(current ?? '');
  async function save() {
    setBusy(true); setErr('');
    try { await post('/comps/cap', { club, neverPaidCountMax: Number(val) }); onSaved(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <input type="number" min="0" value={val} onChange={e => setVal(e.target.value)}
        style={{ width: 64, background: '#1f2937', color: 'inherit', border: '1px solid #374151', borderRadius: 4, padding: '2px 6px' }} />
      {dirty && val !== '' && <button className="btn btn-sm" disabled={busy} onClick={save}>{busy ? '…' : 'Save'}</button>}
      {err && <span style={{ color: '#ef4444', fontSize: 11 }}>{err}</span>}
    </span>
  );
}

// Never-paid memberships per club against a cap that is a number (staff, coaches and Ambassadors all count toward it).
function CapOverview() {
  const { get } = useApi();
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    setLoading(true); setError(null);
    get(`/comps/overview?period=${period}&n=8`).then(setData).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps -- get from useApi() is a new reference every render (see PendingActivityQueue)
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ padding: 16 }}>Loading…</div>;
  if (error) return <div style={{ padding: 16, color: '#ef4444' }}>{error}</div>;
  const clubs = data?.clubs || {};
  const keys = CLUB_ORDER.filter(k => clubs[k]?.length);
  const latest = k => clubs[k][clubs[k].length - 1];
  const prev = k => (clubs[k].length > 1 ? clubs[k][clubs[k].length - 2] : null);
  const th = { padding: '4px 8px', textAlign: 'right' };
  const td = { padding: '6px 8px', textAlign: 'right' };
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 700 }}>Memberships that have never paid, against each club's cap</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['week', 'month'].map(p => (
            <button key={p} className={`module-tab ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>{p === 'week' ? 'Weekly' : 'Monthly'}</button>
          ))}
        </div>
      </div>
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: '#888', fontSize: 11 }}>
            <th style={{ ...th, textAlign: 'left' }}>Club</th>
            <th style={th}>Never paid</th>
            <th style={th}>vs last {period}</th>
            <th style={th}>% of non-Welld</th>
            <th style={th}>% of all memberships</th>
            <th style={th}>Cap</th>
            <th style={th}>Over cap</th>
            <th style={{ ...th, textAlign: 'left' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {keys.map(k => {
            const b = latest(k); const p = prev(k);
            const delta = p ? b.neverPaid - p.neverPaid : null;
            return (
              <tr key={k} style={{ borderTop: '1px solid #2a3340', fontWeight: k === 'ALL' ? 700 : 400 }}>
                <td style={{ ...td, textAlign: 'left' }}>{CLUB_NAMES[k] || k}</td>
                <td style={td}>{b.neverPaid}</td>
                <td style={{ ...td, color: delta == null ? '#666' : delta < 0 ? '#22c55e' : delta > 0 ? '#ef4444' : '#888' }}>{delta == null ? '–' : delta > 0 ? `+${delta}` : delta}</td>
                <td style={td}>{b.pctNeverPaid != null ? `${b.pctNeverPaid}%` : '–'}</td>
                <td style={td}>{b.pctOfTotal != null ? `${b.pctOfTotal}%` : '–'}</td>
                <td style={td}>{k === 'ALL' ? (b.targetCount ?? '–') : <CapEditor club={k} current={b.targetCount} onSaved={load} />}</td>
                <td style={{ ...td, color: b.overTarget > 0 ? '#ef4444' : '#22c55e' }}>{b.overTarget == null ? '–' : b.overTarget > 0 ? b.overTarget : 'none'}</td>
                <td style={{ ...td, textAlign: 'left' }}><FlagPill flag={b.flag} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: '#666', marginTop: 10, lineHeight: 1.5 }}>
        Never paid = PlayByPoint shows $0 lifetime payments (Welld is tracked separately, and a free membership held by someone who pays a Nationwide Unlimited elsewhere,
        or who paid at Denton, is not counted). The cap is a number of memberships and covers staff, coaches and Ambassadors. Status is green at or under the cap, amber up to 25% over.
        Figures are as of the latest nightly run{keys.length ? ` (${latest(keys[0]).asOf})` : ''}. Set a club's cap in the Cap column; the all-clubs cap is the sum.
      </div>
    </div>
  );
}

function MonthlyFloorBadge({ meetsFloor, days }) {
  const ok = meetsFloor;
  return (
    <span style={{
      background: ok ? '#16a34a22' : '#dc262622',
      color: ok ? '#22c55e' : '#ef4444',
      border: `1px solid ${ok ? '#22c55e' : '#ef4444'}`,
      borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
    }} title="Qualifying activity days in the last 30 — private court reservations don't count per the real Ambassador Agreement">
      {days}/mo{ok ? '' : ' ⚠'}
    </span>
  );
}

function PendingActivityQueue() {
  const { get, post } = useApi();
  const { currentClub } = useClub();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const clubId = currentClub?.location_id || currentClub?.id;
    const qs = clubId && clubId !== 'ALL' ? `?locationId=${clubId}` : '';
    get(`/ambassador/pending-activity${qs}`)
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- get from useApi() is a new reference every render; including it causes an infinite fetch loop (confirmed live, 9/30/26)

  useEffect(() => { load(); }, [load]);

  async function review(id, decision) {
    setBusyId(id);
    try {
      await post(`/ambassador/activity/${id}/review`, { decision });
      setRows(rs => rs.filter(r => r.id !== id));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <div style={{ padding: 16, color: 'var(--text-muted)' }}>Loading…</div>;
  if (error) return <div style={{ padding: 16, color: '#ef4444' }}>{error}</div>;
  if (rows.length === 0) return <div style={{ padding: 16, color: 'var(--text-muted)' }}>Nothing pending approval.</div>;

  return (
    <div style={{ padding: 16 }}>
      {rows.map(r => (
        <div key={r.id} style={{
          background: 'var(--surface-2, #111)', border: '1px solid var(--border, #222)',
          borderRadius: 8, marginBottom: 8, padding: '10px 14px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              {r.ambassador_name || r.ambassador_email} <span style={{ color: '#888', fontWeight: 400 }}>· {r.location_id}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {r.action_type.replace(/_/g, ' ')} · {r.points_awarded} pt{r.points_awarded === 1 ? '' : 's'}
              {r.detail ? ` · ${r.detail}` : ''}
            </div>
            <div style={{ fontSize: 10, color: '#666' }}>
              Submitted {new Date(r.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              disabled={busyId === r.id}
              onClick={() => review(r.id, 'approved')}
              style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >Approve</button>
            <button
              disabled={busyId === r.id}
              onClick={() => review(r.id, 'rejected')}
              style={{ background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
            >Reject</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Roster() {
  const { get } = useApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    get('/ambassador/roster').then(setRows).catch(e => setError(e.message)).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- get from useApi() is a new reference every render; including it causes an infinite fetch loop (confirmed live, 9/30/26)

  if (loading) return <div style={{ padding: 16, color: 'var(--text-muted)' }}>Loading…</div>;
  if (error) return <div style={{ padding: 16, color: '#ef4444' }}>{error}</div>;

  return (
    <div style={{ padding: 16, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: '#888', fontSize: 11, textTransform: 'uppercase' }}>
            <th style={{ padding: '4px 8px' }}>Ambassador</th>
            <th style={{ padding: '4px 8px' }}>Club</th>
            <th style={{ padding: '4px 8px' }}>This Quarter</th>
            <th style={{ padding: '4px 8px' }}>Pending</th>
            <th style={{ padding: '4px 8px' }}>Monthly Floor</th>
            <th style={{ padding: '4px 8px' }}>Contract</th>
            <th style={{ padding: '4px 8px' }}>Last Review</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.member_id} style={{ borderTop: '1px solid var(--border, #222)' }}>
              <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.full_name}</td>
              <td style={{ padding: '6px 8px', color: '#888' }}>{r.location_id}</td>
              <td style={{ padding: '6px 8px' }}>{r.approved_points_this_quarter} pts</td>
              <td style={{ padding: '6px 8px', color: r.pending_activity_count > 0 ? '#f59e0b' : '#666' }}>
                {r.pending_activity_count || '—'}
              </td>
              <td style={{ padding: '6px 8px' }}>
                <MonthlyFloorBadge meetsFloor={r.meetsMonthlyFloor} days={r.qualifying_days_30d} />
              </td>
              <td style={{ padding: '6px 8px', color: '#888' }}>{r.contract_status || 'none on file'}</td>
              <td style={{ padding: '6px 8px', color: '#888' }}>
                {r.last_review_at ? new Date(r.last_review_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'never'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: '#666', marginTop: 10 }}>
        "Monthly Floor" checks the real Ambassador Agreement's 4x/month requirement (open play/league/tournament/clinic/own
        program — private court reservations don't count). This is a floor estimate, not a guaranteed-accurate count — see
        the build spec for the known activity_bookings/visits data-linkage gap.
      </div>
    </div>
  );
}

export default function AmbassadorModule() {
  const { isAdmin } = useAuth();
  const tabs = isAdmin ? [OVERVIEW_TAB, ...TABS] : TABS;
  const [tab, setTab] = useState(isAdmin ? 'overview' : 'pending');
  return (
    <div className="module-page">
      <div className="module-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: 22 }}>🎾</span>
          <h1 className="module-title">{isAdmin ? 'Comps and Ambassadors' : 'Ambassadors'}</h1>
        </div>
      </div>

      <div className="module-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`module-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && isAdmin && <CapOverview />}
      {tab === 'pending' && <PendingActivityQueue />}
      {tab === 'roster'  && <Roster />}
    </div>
  );
}
